import React, { useEffect, useState, useCallback } from 'react';
import { Download, Search, Play, RefreshCw, CheckCircle, Clock, AlertTriangle, XCircle } from 'lucide-react';
import api from '../api';
import { NafCodeGroup, ImportJob } from '../types';

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const PappersImportPage: React.FC = () => {
  const [nafGroups, setNafGroups] = useState<NafCodeGroup[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [region, setRegion] = useState('');
  const [department, setDepartment] = useState('');
  const [minCreationDate, setMinCreationDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search results
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchTotal, setSearchTotal] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  // Import
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState('');

  // Jobs
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  const fetchNafGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/naf-codes/groups');
      setNafGroups(res.data.data || res.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des groupes NAF.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchImportJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await api.get('/pappers/import-jobs');
      setImportJobs(res.data.data || res.data || []);
    } catch (err) {
      // ignore
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNafGroups();
    fetchImportJobs();
  }, [fetchNafGroups, fetchImportJobs]);

  const handleToggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  const handleSearch = async () => {
    if (selectedGroupIds.length === 0) {
      setSearchError('Veuillez sélectionner au moins un groupe NAF.');
      return;
    }
    setSearchLoading(true);
    setSearchError('');
    setSearchResults([]);
    setHasSearched(true);
    try {
      const params: any = { nafGroupIds: selectedGroupIds.join(',') };
      if (region) params.region = region;
      if (department) params.department = department;
      if (minCreationDate) params.minCreationDate = minCreationDate;

      const res = await api.get('/pappers/search', { params });
      const data = res.data;
      if (Array.isArray(data)) {
        setSearchResults(data);
        setSearchTotal(data.length);
      } else {
        setSearchResults(data.results || data.data || []);
        setSearchTotal(data.total || data.results?.length || data.data?.length || 0);
      }
    } catch (err: any) {
      setSearchError(err.response?.data?.message || 'Erreur lors de la recherche.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleImport = async () => {
    if (selectedGroupIds.length === 0) {
      setImportMessage('Veuillez sélectionner au moins un groupe NAF.');
      return;
    }
    setImportLoading(true);
    setImportMessage('');
    try {
      const payload: any = { nafGroupIds: selectedGroupIds };
      if (region) payload.region = region;
      if (department) payload.department = department;
      if (minCreationDate) payload.minCreationDate = minCreationDate;

      await api.post('/pappers/import', payload);
      setImportMessage('Import lancé avec succès. Consultez le statut ci-dessous.');
      fetchImportJobs();
    } catch (err: any) {
      setImportMessage(err.response?.data?.message || 'Erreur lors de l\'import.');
    } finally {
      setImportLoading(false);
    }
  };

  const getJobStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge badge-success"><CheckCircle size={12} /> Terminé</span>;
      case 'processing':
      case 'running':
        return <span className="badge badge-info"><Clock size={12} /> En cours</span>;
      case 'pending':
        return <span className="badge badge-warning"><Clock size={12} /> En attente</span>;
      case 'failed':
      case 'error':
        return <span className="badge badge-danger"><XCircle size={12} /> Erreur</span>;
      default:
        return <span className="badge badge-gray">{status}</span>;
    }
  };

  if (loading) {
    return <div className="spinner-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <span className="inline-flex"><Download size={28} /> Import Pappers</span>
        </h1>
      </div>

      {error && (
        <div className="login-error mb-3">{error}</div>
      )}

      <div className="two-col">
        <div>
          {/* NAF Group selection */}
          <div className="card mb-3">
            <div className="card-header">
              <h3>Groupes de codes NAF</h3>
              <span className="text-secondary" style={{ fontSize: '0.85rem' }}>
                {selectedGroupIds.length} sélectionné{selectedGroupIds.length > 1 ? 's' : ''}
              </span>
            </div>
            <div className="card-body">
              {nafGroups.length === 0 ? (
                <p className="text-secondary text-center" style={{ padding: '20px' }}>
                  Aucun groupe NAF configuré. Ajoutez des groupes depuis l'administration.
                </p>
              ) : (
                <div className="checkbox-grid">
                  {nafGroups.map((group) => (
                    <div
                      key={group.id}
                      className={`checkbox-item ${selectedGroupIds.includes(group.id) ? 'checked' : ''}`}
                      onClick={() => handleToggleGroup(group.id)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedGroupIds.includes(group.id)}
                        onChange={() => handleToggleGroup(group.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div>
                        <div className="font-semibold">{group.name}</div>
                        {group.description && (
                          <div className="text-secondary" style={{ fontSize: '0.8rem' }}>{group.description}</div>
                        )}
                        <div className="text-secondary" style={{ fontSize: '0.75rem' }}>
                          {group.items?.length || 0} code{(group.items?.length || 0) > 1 ? 's' : ''} NAF
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-3">
            <div className="card-header"><h3>Filtres</h3></div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Région</label>
                  <input
                    className="form-input"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="Ex: Île-de-France"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Département</label>
                  <input
                    className="form-input"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    placeholder="Ex: 75"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Date de création minimum</label>
                <input
                  type="date"
                  className="form-input"
                  value={minCreationDate}
                  onChange={(e) => setMinCreationDate(e.target.value)}
                  style={{ maxWidth: '250px' }}
                />
              </div>
            </div>
            <div className="card-footer">
              <button className="btn" onClick={handleSearch} disabled={searchLoading}>
                {searchLoading ? <span className="spinner spinner-sm" /> : <><Search size={16} /> Rechercher</>}
              </button>
              <button className="btn btn-primary" onClick={handleImport} disabled={importLoading}>
                {importLoading ? <span className="spinner spinner-sm" /> : <><Play size={16} /> Importer</>}
              </button>
            </div>
          </div>

          {importMessage && (
            <div
              className={`mb-3 ${importMessage.includes('succès') ? 'badge-success' : 'badge-danger'}`}
              style={{ padding: '10px 16px', borderRadius: '8px', fontSize: '0.9rem' }}
            >
              {importMessage}
            </div>
          )}
        </div>

        <div>
          {/* Search Results Preview */}
          {hasSearched && (
            <div className="card mb-3">
              <div className="card-header">
                <h3>Résultats de recherche</h3>
                <span className="badge badge-info">{searchTotal} résultat{searchTotal > 1 ? 's' : ''}</span>
              </div>
              <div className="card-body">
                {searchLoading ? (
                  <div className="spinner-container"><div className="spinner" /></div>
                ) : searchError ? (
                  <div className="empty-state">
                    <AlertTriangle size={36} />
                    <p className="text-secondary">{searchError}</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="empty-state">
                    <Search size={36} />
                    <p className="text-secondary">Aucun résultat</p>
                  </div>
                ) : (
                  <div className="import-preview">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>SIREN</th>
                          <th>Nom</th>
                          <th>Ville</th>
                          <th>NAF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.slice(0, 50).map((r: any, idx: number) => (
                          <tr key={r.siren || idx}>
                            <td>{r.siren || '-'}</td>
                            <td>{r.nom_entreprise || r.name || r.denomination || '-'}</td>
                            <td>{r.ville || r.siege?.ville || r.addressCity || '-'}</td>
                            <td>{r.code_naf || r.nafCode || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {searchResults.length > 50 && (
                      <p className="text-secondary text-center mt-2" style={{ fontSize: '0.85rem' }}>
                        Affichage des 50 premiers résultats sur {searchTotal}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Import Jobs */}
          <div className="card">
            <div className="card-header">
              <h3>Historique des imports</h3>
              <button className="btn btn-sm" onClick={fetchImportJobs} disabled={jobsLoading}>
                <RefreshCw size={14} /> Actualiser
              </button>
            </div>
            <div className="card-body">
              {jobsLoading ? (
                <div className="spinner-container"><div className="spinner" /></div>
              ) : importJobs.length === 0 ? (
                <div className="empty-state" style={{ padding: '20px' }}>
                  <p className="text-secondary">Aucun import</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {importJobs.map((job) => (
                    <div key={job.id} className="card" style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span className="font-semibold">{job.type || 'Import Pappers'}</span>
                        {getJobStatusBadge(job.status)}
                      </div>
                      <div className="import-stats">
                        <div className="import-stat">
                          <div className="import-stat-value">{job.totalRecords}</div>
                          <div className="import-stat-label">Total</div>
                        </div>
                        <div className="import-stat">
                          <div className="import-stat-value">{job.processedRecords}</div>
                          <div className="import-stat-label">Traités</div>
                        </div>
                        <div className="import-stat">
                          <div className="import-stat-value">{job.createdRecords}</div>
                          <div className="import-stat-label">Créés</div>
                        </div>
                        <div className="import-stat">
                          <div className="import-stat-value">{job.duplicateRecords}</div>
                          <div className="import-stat-label">Doublons</div>
                        </div>
                      </div>
                      {job.totalRecords > 0 && (
                        <div className="progress-bar">
                          <div
                            className="progress-bar-fill"
                            style={{ width: `${Math.round((job.processedRecords / job.totalRecords) * 100)}%` }}
                          />
                        </div>
                      )}
                      <div className="text-secondary mt-1" style={{ fontSize: '0.8rem' }}>
                        {formatDate(job.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PappersImportPage;
